<script lang="ts">
    import { onMount } from 'svelte';
    import * as t from '../../translations/Petitions.ts';
    import { getPetitions, getPetitionUserUrlToken, getSignedPetitionIdsForUser, type Petition, type PetitionsResponse } from '../../api/petitions.ts';
    import { getUser } from '../../api/users.ts';
    import { vvSettings, associationIdName } from '../../api/common.ts';
    import Pagination from '../../lib/Pagination.svelte';
    import EmptyState from '../../lib/Petitions/EmptyState.svelte';
    import PetitionCard from '../../lib/Petitions/PetitionCard.svelte';
    import SpotlightGrid from '../../lib/Petitions/SpotlightGrid.svelte';
    import SkeletonLoader from '../../lib/Petitions/SkeletonLoader.svelte';

    // Extended vvSettings type for petitions mock data
    interface PetitionsSettings {
        petitions?: PetitionsResponse;
    }
    const petitionsSettings = vvSettings as typeof vvSettings & PetitionsSettings;

    let petitions: Petition[] = [];
    let spotlightPetitions: Petition[] = [];
    let signedPetitionIds: Set<number> = new Set();
    let loading = true;
    let error = '';
    let currentPage = 1;
    const itemsPerPage = 10;

    $: allPetitions = [...spotlightPetitions, ...petitions];
    $: visiblePetitions = allPetitions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    $: totalItems = allPetitions.length;

    function setPage(page: number) {
        currentPage = page;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    /** Full-page navigation to the canonical MVC route so server-side tracking tags render. */
    function buildRespondUrl(petitionId: number, fromSingle = false): string {
        const q = fromSingle ? '?fromSingle=1' : '';
        return `/${associationIdName}${getSiteSegment()}/Petitions/${petitionId}/Respond${q}`;
    }

    function handleSignPetition(petition: Petition) {
        window.location.href = buildRespondUrl(petition.id);
    }

    async function resolvePetitionUserToken(): Promise<string | undefined> {
        try {
            const rememberedUser = await getUser();
            const token = rememberedUser?.userToken;
            // Prefer API source-of-truth to avoid stale vvSettings token after cross-page user switches.
            if (typeof token === 'string' && token.trim().length > 0) return token.trim();
            return undefined;
        } catch {
            // Fallback only when remembered-user lookup fails.
            const tokenFromSettings = getPetitionUserUrlToken();
            return tokenFromSettings || undefined;
        }
    }

    async function loadSignedStateForPetitions(spotlight: Petition[], others: Petition[]) {
        const token = await resolvePetitionUserToken();
        const ids = [...spotlight, ...others].map((p) => p.id);
        signedPetitionIds = await getSignedPetitionIdsForUser(token, ids);
    }

    /** Auto-redirect to the canonical server route so tracking tags render; skip if we're already on Respond. */
    function tryPushSinglePetitionSign(list: Petition[]): boolean {
        if (list.length !== 1) return false;
        const target = buildRespondUrl(list[0].id, true);
        if (window.location.pathname.toLowerCase().endsWith(`/petitions/${list[0].id}/respond`)) {
            return true;
        }
        window.location.replace(target);
        return true;
    }

    onMount(async () => {
        loading = true;
        error = '';
        
        // Check if mock data is provided via vvSettings
        if (petitionsSettings.petitions) {
            spotlightPetitions = petitionsSettings.petitions.spotlightPetitions || [];
            petitions = petitionsSettings.petitions.otherPetitions || [];
            const singlePetition = [...spotlightPetitions, ...petitions];
            if (tryPushSinglePetitionSign(singlePetition)) {
                return;
            }
            await loadSignedStateForPetitions(spotlightPetitions, petitions);
            loading = false;
            return;
        }
        
        try {
            const response: PetitionsResponse = await getPetitions();
            spotlightPetitions = response.spotlightPetitions || [];
            petitions = response.otherPetitions || [];
            const singlePetition = [...spotlightPetitions, ...petitions];
            if (tryPushSinglePetitionSign(singlePetition)) {
                return;
            }
            await loadSignedStateForPetitions(spotlightPetitions, petitions);
        } catch (err) {
            console.error('Failed to fetch petitions:', err);
            error = t.Error_Message();
        } finally {
            loading = false;
        }
    });
</script>

<div class="@container petition-list-page w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 flex flex-col gap-8 interfont">
    <!-- Page Header -->
    <div class="flex flex-col gap-2">
        <h1 class="text-3xl md:text-4xl">{t.Page_Header()}</h1>
    </div>

    <!-- Spotlight Grid - Featured Petitions -->
    {#if !loading && spotlightPetitions.length > 0}
        <div class="w-full">
            <SpotlightGrid petitions={spotlightPetitions} {signedPetitionIds} onSign={handleSignPetition} />
        </div>
    {/if}

    <!-- Content Area -->
    <div class="mt-2">
        {#if loading}
            <SkeletonLoader count={3} />
        {:else if error}
            <p class="text-red-500 text-center py-8">{error}</p>
        {:else if allPetitions.length === 0}
            <EmptyState />
        {:else}
            <!-- Petitions List -->
            <div class="w-full flex flex-col gap-6">
                {#each visiblePetitions as petition (petition.id)}
                    <div>
                        <PetitionCard {petition} hasSigned={signedPetitionIds.has(petition.id)} onSign={handleSignPetition} />
                    </div>
                {/each}
            </div>
            
            <!-- Pagination -->
            <Pagination 
                {totalItems} 
                {currentPage} 
                {itemsPerPage} 
                onPageChange={setPage} 
            />
        {/if}
    </div>
</div>

<style>
    :global(.petition-list-page h1) {
        font-size: 2rem;
        line-height: 2.5rem;
        color: #111827;
        font-weight: 700;
    }
</style>

